import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { serviceNavItems } from '../../../data/services';

const LOGO = 'http://geel.fp.08wx.cn/images/e33b4806-4fc4-48a2-93dd-68e0d55a6c41.png';

const LANGUAGES = [
  { code: 'zh-CN', label: '简中' },
  { code: 'en', label: 'EN' },
  { code: 'zh-TW', label: '繁中' },
];

const productItems = [
  { labelKey: 'nav.products.overview', path: '/products' },
  { labelKey: 'nav.products.cex', path: '/products/cex' },
  { labelKey: 'nav.products.rwa', path: '/products/rwa' },
  { labelKey: 'nav.products.wallet', path: '/products/wallet' },
  { labelKey: 'nav.products.staking', path: '/products/staking' },
  { labelKey: 'nav.products.mining', path: '/products/mining' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileProdOpen, setMobileProdOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language || 'zh-CN');
  const productsDropdownRef = useRef<HTMLDivElement>(null);
  const servicesDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (productsDropdownRef.current && !productsDropdownRef.current.contains(e.target as Node)) {
        setProductsOpen(false);
      }
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProductsOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setCurrentLang(lng);
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  const changeLang = (code: string) => {
    void i18n.changeLanguage(code);
  };

  const navLinks = [
    { labelKey: 'nav.home', path: '/' },
    { labelKey: 'nav.about', path: '/about' },
    { labelKey: 'nav.advantages', path: '/advantages' },
    { labelKey: 'nav.news', path: '/news' },
    { labelKey: 'nav.contact', path: '/contact' },
  ];

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? 'bg-white/95 backdrop-blur-xl border-b border-primary-200'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 h-[72px] flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 flex-shrink-0">
          <div className="w-[10.5rem] h-[5.5rem] flex items-center justify-center">
            <img src={LOGO} alt="Logo" className="w-full h-full object-contain" />
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.slice(0, 3).map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap ${
                isActive(link.path)
                  ? 'text-primary'
                  : 'text-gray-600 hover:text-primary hover:bg-primary-50'
              }`}
            >
              {t(link.labelKey)}
            </Link>
          ))}

          {/* Products Dropdown */}
          <div ref={productsDropdownRef} className="relative">
            <button
              className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/products')
                  ? 'text-primary'
                  : 'text-gray-600 hover:text-primary hover:bg-primary-50'
              }`}
              onMouseEnter={() => {
                setServicesOpen(false);
                setProductsOpen(true);
              }}
              onClick={() => setProductsOpen(!productsOpen)}
            >
              {t('nav.products')}
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`} />
            </button>
            {productsOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-52 bg-white border border-primary-200 rounded-xl overflow-hidden"
                onMouseLeave={() => setProductsOpen(false)}
              >
                {productItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-2 px-4 py-3 text-sm text-gray-600 hover:text-primary hover:bg-primary-50 transition-colors duration-150 border-b border-primary-100 last:border-0"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0" />
                    {t(item.labelKey)}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Services Dropdown */}
          <div ref={servicesDropdownRef} className="relative">
            <button
              className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/services')
                  ? 'text-primary'
                  : 'text-gray-600 hover:text-primary hover:bg-primary-50'
              }`}
              onMouseEnter={() => {
                setProductsOpen(false);
                setServicesOpen(true);
              }}
              onClick={() => setServicesOpen(!servicesOpen)}
            >
              {t('nav.services')}
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {servicesOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-white border border-primary-200 rounded-xl overflow-hidden"
                onMouseLeave={() => setServicesOpen(false)}
              >
                <Link
                  to="/services"
                  className="flex items-center gap-2 px-4 py-3 text-sm text-gray-600 hover:text-primary hover:bg-primary-50 transition-colors duration-150 border-b border-primary-100"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0" />
                  {t('nav.services.overview')}
                </Link>
                {serviceNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-2 px-4 py-3 text-sm text-gray-600 hover:text-primary hover:bg-primary-50 transition-colors duration-150 border-b border-primary-100 last:border-0"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0" />
                    {t(item.labelKey)}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {navLinks.slice(3).map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap ${
                isActive(link.path)
                  ? 'text-primary'
                  : 'text-gray-600 hover:text-primary hover:bg-primary-50'
              }`}
            >
              {t(link.labelKey)}
            </Link>
          ))}
        </nav>

        {/* Right: Language + CTA */}
        <div className="hidden lg:flex items-center gap-3">
          {/* Language Switcher */}
          <div className="flex items-center bg-primary-50 border border-primary-200 rounded-full p-1 gap-0.5">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLang(lang.code)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  currentLang === lang.code
                    ? 'bg-primary text-white'
                    : 'text-gray-500 hover:text-primary'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>

          {/* CTA */}
          <Link
            to="/contact"
            className="px-5 py-2 bg-primary hover:bg-primary-dark text-white font-semibold text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('nav.cta')}
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:text-primary cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white/98 backdrop-blur-xl border-t border-primary-100 px-6 pb-6 pt-2">
          {navLinks.slice(0, 3).map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="flex items-center py-3 text-sm font-medium text-gray-600 hover:text-primary border-b border-primary-100 transition-colors"
            >
              {t(link.labelKey)}
            </Link>
          ))}

          {/* Mobile Products */}
          <div className="border-b border-primary-100">
            <button
              className="flex items-center justify-between w-full py-3 text-sm font-medium text-gray-600 cursor-pointer"
              onClick={() => setMobileProdOpen(!mobileProdOpen)}
            >
              {t('nav.products')}
              <i className={`ri-arrow-down-s-line transition-transform ${mobileProdOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileProdOpen && (
              <div className="pb-2 pl-4">
                {productItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-2 py-2 text-sm text-gray-500 hover:text-primary transition-colors"
                  >
                    <span className="w-1 h-1 rounded-full bg-primary/60" />
                    {t(item.labelKey)}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Services */}
          <div className="border-b border-primary-100">
            <button
              className="flex items-center justify-between w-full py-3 text-sm font-medium text-gray-600 cursor-pointer"
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
            >
              {t('nav.services')}
              <i className={`ri-arrow-down-s-line transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileServicesOpen && (
              <div className="pb-2 pl-4">
                <Link
                  to="/services"
                  className="flex items-center gap-2 py-2 text-sm text-gray-500 hover:text-primary transition-colors"
                >
                  <span className="w-1 h-1 rounded-full bg-primary/60" />
                  {t('nav.services.overview')}
                </Link>
                {serviceNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-2 py-2 text-sm text-gray-500 hover:text-primary transition-colors"
                  >
                    <span className="w-1 h-1 rounded-full bg-primary/60" />
                    {t(item.labelKey)}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {navLinks.slice(3).map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="flex items-center py-3 text-sm font-medium text-gray-600 hover:text-primary border-b border-primary-100 transition-colors"
            >
              {t(link.labelKey)}
            </Link>
          ))}

          {/* Mobile Language */}
          <div className="flex items-center gap-2 pt-4">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLang(lang.code)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer ${
                  currentLang === lang.code
                    ? 'bg-primary border-primary text-white'
                    : 'border-primary-200 text-gray-500 hover:text-primary'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>

          <Link
            to="/contact"
            className="mt-4 w-full flex items-center justify-center py-3 bg-primary hover:bg-primary-dark text-white font-semibold text-sm rounded-full transition-all cursor-pointer"
          >
            {t('nav.cta')}
          </Link>
        </div>
      )}
    </header>
  );
}
